import React, { useState, useEffect, useCallback } from "react";
import { Box, IconButton, Tab, ThemeProvider, Tooltip } from "@mui/material";
import {
  DOCUMENT_TYPES,
  fetchDocumentNumber,
  fetchUnitOptionsForItems,
  getDocumentConfig,
  MODIFICATION_TYPES,
  theme,
  tooltipContent,
} from "../config/DocumentConfig";
import { HelpOutline } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Loader, Printer, Save, View } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { getTodayDate } from "../../utils/constants";
import { purchaseData } from "../salesInvoice/data";
import { calculateRowValues } from "../config/SalesInvoiceTableConfig";
import TimelineTab from "../salesInvoice/TimelineTab";
import ModificationCommentModal from "../salesInvoice/ModificationCommentModal";
import Modal from "../common/CommonModal";
import SummaryTotalCard from "../salesInvoice/SummaryTotalCard";
import ConfigCard from "../salesInvoice/ConfigCard";
import { calculateSampleIssueRow } from "../config/SampleIssueTableConfig";
import { getWorkOrderVatPercent } from "../config/WorkOrderTableConfig";
import ModeIndicator from "../salesInvoice/ModeIndicator";
import LocationTransferGeneral from "./LocationTransferGeneral";
import LocationTransferTable from "./LocationTransferTable";
import FloatingItemInformation from "../salesInvoice/FloatingItemInformation";
import checkIfUserHasAccess from "../common/UserCheckAccess";

const LocationTransfer = ({
  documentType = DOCUMENT_TYPES.LOCATION_TRANSFER,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();
  const config = getDocumentConfig(documentType);

  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "DOODDEMO";
  const CURRENT_USER = localStorage?.getItem("currentUserName") || "admin";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const todayDate = getTodayDate();
  const documentNoFromState = location?.state?.docNo || null;
  const modeFromState = location?.state?.mode || "new";
  const transferredData = location?.state;
  // console.log("Transferred Data to Sample Issue:", transferredData);
  const [focusedProductCode, setFocusedProductCode] = useState(null);
  const [currentStockInhand, setCurrentStockInhand] = useState(null);


  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(modeFromState);
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString(),
  );
  const [parentTabValue, setParentTabValue] = useState("1");
  const [supplierData, setSupplierData] = useState(null);
  const [items, setItems] = useState(purchaseData);
  const [currentLocation, setCurrentLocation] = useState(
    localStorage.getItem("departmentLocation") || "A",
  );
  const [showSidebar, setShowSidebar] = useState(false);
  const [initialRowUnitOptions, setInitialRowUnitOptions] = useState({});
  const [glSettings, setGlSettings] = useState([]);

  const [discountAmount, setDiscountAmount] = useState("");
  const [roundOff, setRoundOff] = useState("");

  const [autoVatIsOn, setAutoVatIsOn] = useState(true);
  const [vatAmountManual, setVatAmountManual] = useState(null);
  const [transferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isModificationModalOpen, setIsModificationModalOpen] = useState(false);

  const [drAccount, setDrAccount] = useState("");
  const [drDesc, setDrDesc] = useState("");
  const [crAccount, setCrAccount] = useState("");
  const [crDesc, setCrDesc] = useState("");

  const handleParentTabChange = (event, newValue) => {
    setParentTabValue(newValue);
  };

  const [documentData, setDocumentData] = useState({
    documentNo: "",
    date: todayDate,
    proposalNo: "",
    salesperson: "",
    salespersonId: "",
    salespersonAcc: "",
    salespersonAccDesc: "",
    customer: "",
    customerAcc: "",
    remarks: "",
    discountAmount: 0,
    roundOff: 0,
    taxableAmount: 0,
    vatAmount: 0,
    grossAmount: 0,
    netAmount: 0,
    bcAmt: 0,
    vatStatus: "",
    vatPercent: 0,
    approval: "Pending",
    currentStatus: "Normal",
    cordinatorid: "",
    cordinatorName: "",
    from_location_name: "",
    to_location_name: "",
  });


  const calculateTotals = () => {
    const round = (val) => Math.round(Number(val) * 100) / 100;

    const processedItems = items.map((item) =>
      calculateRowValues(item, documentData, {
        items,
      }),
    );

    const rowTaxableSum = round(
      processedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    );

    const rowVatSum = round(
      processedItems.reduce(
        (sum, item) => sum + (Number(item.vatAmount) || 0),
        0,
      ),
    );
    const grossAmount = round(
      processedItems.reduce((sum, item) => sum + (Number(item.total) || 0), 0),
    );

    const globalDiscount = Number(discountAmount) || 0;
    const roundOffValue = Number(roundOff) || 0;

    const taxableAmount = round(rowTaxableSum - globalDiscount - roundOffValue);

    const totalVat =
      !autoVatIsOn && vatAmountManual !== null
        ? round(vatAmountManual)
        : rowVatSum;

    const totalAmount = round(taxableAmount + totalVat);

    const netAmount = round(totalAmount);

    const currencyRate = Number(documentData.currencyRate) || 1;
    const bcAmt = round(netAmount * currencyRate);

    return {
      grossAmount: grossAmount.toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      vatAmount: totalVat.toFixed(2),
      discountAmount: globalDiscount.toFixed(2),
      roundOff: roundOffValue.toFixed(2),
      netAmount: netAmount.toFixed(2),
      bcAmt: bcAmt.toFixed(2),
    };
  };

  const totals = calculateTotals();

  // Auto-fill from transferred data (QuotationDrawer state)
  useEffect(() => {
    if (transferredData?.proposalNumber && transferredData?.sampleIssueData) {
      const { proposalNumber, sampleIssueData, selectedItems } =
        transferredData;

      // Auto-fill documentData with sample issue data
      setDocumentData((prev) => ({
        ...prev,
        proposalNo: proposalNumber || "",
        customer: sampleIssueData?.customer || "",
        customerAcc: sampleIssueData?.cust_acc || "",
        salesperson: sampleIssueData?.salesperson || "",
        salespersonId: sampleIssueData?.salespersonId || "",
        salespersonAcc: sampleIssueData?.salespersonAcc || "",
        salespersonAccDesc: sampleIssueData?.salesPersonAccDesc || "",
        date: sampleIssueData?.date || todayDate,
        cordinatorid: sampleIssueData?.cordinatorId || "",
        cordinatorName: sampleIssueData?.cordinatorName || "",
      }));

      // Auto-fill Dr Account with salespersonAcc
      setDrAccount(sampleIssueData?.salespersonAcc || "");
      setDrDesc(sampleIssueData?.salesPersonAccDesc || "");

      // Auto-fill items from selected quotation items
      if (selectedItems && selectedItems.length > 0) {
        const mappedItems = selectedItems.map((item, index) => ({
          id: Date.now() + index,
          sino: index + 1,
          code: item.Code || "",
          description: item.Description || "",
          qty: Number(item.Sample_Qty) || 0,
          unit: item.Unit || "PCS",
          unitPrice: Number(item.unitPrice) || 0,
          discountPercent: Number(item.discountPercent) || 0,
          amount: Number(item.amount) || 0,
          fraction: item.fraction || "1",
          vatPercent: Number(item.vatPercent) || 5,
          vatAmount: Number(item.vatAmount) || 0,
          total: Number(item.total) || 0,
          location: item.locn || "A",
          oem: item.oem || "",
          status: item.Status || "",
          cntrl: item.soUid || "",
        }));

        setItems(mappedItems);
      }

      console.log("Auto-filled documentData and items from transfer state");
    }
  }, [transferredData, todayDate]);

  const fetchGLSettings = useCallback(async () => {
    const url = `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/SCRN_CODE/DM/-/${DEPARTMENT}/-`;

    try {
      const response = await request({ url, method: "GET", timeout: 10000 });

      const settings = response || [];
      setGlSettings(settings);

      const credit = settings?.find((item) => item.Scrn_code === "DM-STK-C");
      if (credit) {
        setCrAccount(credit.ACCOUNT || "");
        setCrDesc(credit.accdesc || "");
      }
    } catch (error) {
      console.error("Failed to fetch GL settings", error);
      setGlSettings([]);

      // Reset if fetch fails
      setCrAccount("");
      setCrDesc("");
    }
  }, [BASE_URL, COMPANY_CODE]);

  useEffect(() => {
    if (mode === "new") {
      fetchGLSettings();
    }
  }, [mode, fetchGLSettings]);

  // Sync currentLocation and items' locations with from-location changes
  useEffect(() => {
    if (documentData.salespersonId) {
      setCurrentLocation(documentData.salespersonId);
      setItems(prevItems =>
        prevItems.map(item => ({
          ...item,
          location: documentData.salespersonId,
        })),
      );
    }
  }, [documentData.salespersonId]);

  // useEffect(() => {
  //   if (documentData.salespersonAcc) {
  //     setDrAccount(documentData.salespersonAcc);
  //   }
  // }, [documentData.salespersonAcc]);
  const refreshStockDocument = async (
    companyCode,
    documentNumber,
    department,
    fieldMap,
  ) => {
    const response = await fetch(
      `${BASE_URL}/api/CRMStockRefreshDoc/${companyCode}/LT/${documentNumber}/${department}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Stock refresh failed");
    }

    return response.json();
  };

  const handleSave = async () => {
    try {
      if (
        documentData.salespersonId?.trim() === documentData.cordinatorid?.trim()
      ) {
        alert("From and To Location Cannot be Same");
      }

      setIsSaving(true);

      // Validate items
      const filteredItems = items.filter(
        (item) => item.code && item.code.trim() !== "",
      );

      if (filteredItems.length === 0) {
        alert("Please add at least one item to the sample issue");
        setIsSaving(false);
        return;
      }

      // if (!documentData.customer) {
      //   alert("Please select a customer");
      //   setIsSaving(false);
      //   return;
      // }

      if (!documentData.documentNo) {
        alert("Location number is required");
        setIsSaving(false);
        return;
      }

      // Calculate totals before saving
      const round = (val) => Math.round(Number(val) * 100) / 100;

      // Map line items to payload format
      const lineItemsPayload = filteredItems.map((item, index) => ({
        Slno: index + 1,
        code: item.code || "",
        description: item.description || "",
        unit: item.unit || "PCS",
        qty: Number(item.qty) || 0,
      }));

      const finalPayloadLatest = filteredItems.map((item, index) => {
        const lineItem = lineItemsPayload[index] || {};
        return {
          CmpCode: COMPANY_CODE,
          mode: documentNoFromState ? "EDIT" : "SAVE",
          loctrno: documentData.documentNo,
          date: documentData.date || todayDate,
          Frm: documentData.salespersonId,
          To: documentData.cordinatorid,
          Frm_name: documentData.from_location_name,
          To_name: documentData.to_location_name,
          Slno: index + 1,
          code: lineItem.code || "",
          description: lineItem.description || "",
          unit: lineItem.unit || "PCS",
          qty: lineItem.qty || 0,
          enteredby: CURRENT_USER,
          deptno: DEPARTMENT,
          Remarks: documentData.remarks || "",
          LocQty: Number(lineItem.qty) || 0,
          Cntrl: "",
          DocType: "L",
          DocDept: DEPARTMENT,
          DocNo: documentData.documentNo,
        };
      });

      console.log("Location Transfer Payload to save:", finalPayloadLatest);

      const apiUrl = `${BASE_URL}${config.apiEndpoints.save}`;

      const response = await request({
        url: apiUrl,
        method: "POST",
        data: finalPayloadLatest,
        timeout: 10000,
      });

      console.log("API Response:", response);
      // 1️⃣ Refresh stock document
      await refreshStockDocument(
        COMPANY_CODE,
        response.invoiceNo, // use property directly
        DEPARTMENT,
      );

      // 2️⃣ Handle success
      if (response.result === "Saved") {
        alert(`Location Transfer Success`);
        console.log("Invoice number:", response.invoiceNo);
        navigate("/location_transfer_list");
      } else if (response.result) {
        alert(`Location Transfer Success`);
        navigate("/location_transfer_list");
      } else {
        alert(
          `Failed to save Location transfer: ${response?.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("Error saving Location Transfer:", error);
      alert("Error saving Location Transfer: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };
  const handlePrint = () => { };
  const fetchDocumentDetail = async (documentNo) => {
    try {
      const url = config.apiEndpoints.detail(
        COMPANY_CODE,
        documentNo,
        DEPARTMENT,
      );
      const fullUrl = `${BASE_URL}${url}`;
      const response = await request({ url: fullUrl, method: "GET" });

      if (response && response.length > 0) {
        const main = response[0];
        console.log("Fetched Sample Issue detail:", main);

        setDocumentData((prev) => ({
          ...prev,
          documentNo: String(
            main.LOCTR_NO || main.inv_no || main[config.fieldMappings.documentNo] || "",
          ),
          date: (main.date || main.tr_date || "").slice(0, 10) || todayDate,
          salesperson: main.FROM || "",
          salespersonId: main.FROM || "",
          cordinatorid: main.TO || "",
          remarks: main.remarks || "",
        }));
        setCurrentLocation(main?.FROM || "");
        console.log("Current location", main?.FROM);
        // Look up location names from the API
        try {
          const locUrl = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/LOCATONLIST_ALL/-/-`;
          const locRes = await request({ url: locUrl, method: "GET", timeout: 10000 });
          if (locRes && locRes.length > 0) {
            const fromLoc = locRes.find((l) => l.loc_code === main.FROM);
            const toLoc = locRes.find((l) => l.loc_code === main.TO);
            setDocumentData((prev) => ({
              ...prev,
              from_location_name: fromLoc?.DESC || "",
              to_location_name: toLoc?.DESC || "",
            }));
          }
        } catch (_) { }

        const fromLocation = main.FROM || currentLocation || "MAIN SHOP";
        const mappedItems = response.map((row, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: (row.code || row.do_icode || "").trim(),
          description: (row.idesc || "").trim(),
          location: (row.locn || "").trim() || fromLocation,
          unit: (row.unit || row.punit || "").trim(),
          qty: row.tr_qty2 || row.tr_qty || row.so_qty || 0,
          originalQty: row.tr_qty2 || row.tr_qty || row.so_qty || 0,
          unitPrice: row.funit_pric || row.unit_price || row.unit_cost || 0,
          discount: row.discount || 0,
          fraction: row.fraction != null ? String(row.fraction) : "1",
          "vat%": row.bcdisc || 0,
          cntrl: row.soGuid || row.it_slno || "",
          amount: row.line_total || row.fline_tota || 0,
        }));

        setItems(mappedItems);
      }
    } catch (err) {
      console.error("Failed to fetch Sample Issue detail:", err);
    }
  };
  const initializeDocument = useCallback(async () => {
    try {
      const { voucherType, documentNo } = await fetchDocumentNumber(
        documentType,
        documentData.paymentMode,
        BASE_URL,
        COMPANY_CODE,
        DEPARTMENT,
      );

      if (documentNo) {
        setDocumentData((prev) => ({
          ...prev,
          documentNo: documentNo,
          voucherType,
        }));
      }
      console.log("Voucher Type:", voucherType, documentNo);
    } catch (err) {
      console.error("Error initializing document:", err);
    }
  }, [
    documentType,
    documentData?.paymentMode,
    BASE_URL,
    COMPANY_CODE,
    DEPARTMENT,
  ]);

  useEffect(() => {
    if (documentNoFromState && request) {
      fetchDocumentDetail(documentNoFromState);
    } else {
      // keep existing behavior for new mode
      setDocumentData((prev) => ({ ...prev, date: todayDate }));
    }
  }, [documentNoFromState]);

  useEffect(() => {
    if (mode === "new") {
      initializeDocument();
    }
  }, [mode, initializeDocument]);

  return (
    <div className="w-full relative">
      <div className="flex items-center justify-between lg:px-10 px-2 mb-2 gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">Location Transfer</h1>
        </div>

        <Tooltip title={tooltipContent} sx={{ margin: 0, padding: 0 }}>
          <IconButton>
            <HelpOutline
              className="text-blue-600 bg-white rounded-full"
              fontSize="medium"
            />
          </IconButton>
        </Tooltip>
      </div>
      <ModeIndicator mode={mode} documentType={documentType} />
      <div className="w-full rounded-lg">
        <div className="lg:px-10">
          <div className="bg-gray-50 border lg:p-3 rounded-lg shadow pb-4">
            <ThemeProvider theme={theme}>
              <TabContext value={parentTabValue}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TabList onChange={handleParentTabChange}>
                    <Tab label="General" value="1" sx={{ padding: 0 }} />
                    <Tab label="Timeline" value="2" sx={{ padding: 0 }} />
                  </TabList>
                  <div className="lg:flex hidden items-center gap-3">
                    <button
                      onClick={() => {
                        navigate("/location_transfer_list");
                      }}
                      className="flex items-center gap-2 px-5 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg shadow hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <View className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        if (mode === "edit") {
                          setIsModificationModalOpen(true);
                        } else {
                          handleSave();
                        }
                      }}
                      disabled={mode === "view" || isSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {mode === "edit" ? "Update" : "Save"}
                        </>
                      )}
                    </button>
                  </div>
                </Box>
                <TabPanel value="1" sx={{ p: 0, paddingX: 0 }}>
                  <LocationTransferGeneral
                    mode={mode}
                    WorkOrderData={documentData}
                    setWorkOrderData={setDocumentData}
                    currentLocation={currentLocation}
                    drAccount={drAccount}
                    setDrAccount={setDrAccount}
                    drDesc={drDesc}
                    setDrDesc={setDrDesc}
                    crAccount={crAccount}
                    setCrAccount={setCrAccount}
                    crDesc={crDesc}
                    setCrDesc={setCrDesc}
                    BASE_URL={BASE_URL}
                    COMPANY_CODE={COMPANY_CODE}
                    DEPARTMENT_NO={DEPARTMENT}
                    type="SAMPLEISSUE"
                    setCurrentLocation={setCurrentLocation}
                    
                  />
                </TabPanel>

                <TabPanel value="2" sx={{ p: 1 }}>
                  <TimelineTab
                    BASE_URL={BASE_URL}
                    DEPARTMENT={DEPARTMENT}
                    modificationType={MODIFICATION_TYPES[documentType] || null}
                    invoiceId={documentNoFromState}
                    COMPANY_CODE={COMPANY_CODE}
                  />
                </TabPanel>
              </TabContext>
            </ThemeProvider>
          </div>
        </div>
        <main className="w-full mt-4 relative">
          <LocationTransferTable
            items={items}
            onItemsChange={setItems}
            setShowSidebar={setShowSidebar}
            currentLocation={currentLocation}
            setCurrentLocation={setCurrentLocation}
            extraFields={[]}
            purchaseData={documentData}
            initialRowUnitOptions={initialRowUnitOptions}
            setInitialRowUnitOptions={setInitialRowUnitOptions}
            calculateWorkOrderRow={calculateSampleIssueRow}
            invoiceData={documentData}
            getVatPercent={getWorkOrderVatPercent}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            DEPARTMENT_NO={DEPARTMENT}
            mode={mode}
            documentType={documentType}
            setFocusedProductCode={setFocusedProductCode}
            currentStockInhand={currentStockInhand}
          />
          {checkIfUserHasAccess("FT") && (
            <FloatingItemInformation
              showSidebar={showSidebar}
              currentLocation={currentLocation}
              companyCode={COMPANY_CODE}
              API_BASE_URL={BASE_URL}
              productCode={focusedProductCode}
              department={DEPARTMENT}
              onStockInhandUpdate={setCurrentStockInhand}
            />
          )}
        </main>
        <div className="w-full lg:px-10 p-1 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            {/* <ConfigCard
              invoiceData={documentData}
              setAutoVatIsOn={setAutoVatIsOn}
              setInvoiceData={setDocumentData}
              autoVatIsOn={autoVatIsOn}
              documentType={documentType}
              config={config}
              setIsModalOpen={setIsTransferModalOpen}
              isModalOpen={transferModalOpen}
              invoiceNoFromState={documentNoFromState}
              accountData={supplierData}
              showCurrency={false}
              showVatAutoCalculation={false}
            /> */}
            {/* <SummaryTotalCard
              totals={totals}
              setInvoiceData={setDocumentData}
              invoiceData={documentData}
              handlePrint={handlePrint}
              handleSave={handleSave}
              roundOff={roundOff}
              setRoundOff={setRoundOff}
              discountAmount={discountAmount}
              setDiscountAmount={setDiscountAmount}
              mode={mode}
              autoVatIsOn={autoVatIsOn}
              setVatAmountManual={setVatAmountManual}
              config={config}
              setIsModificationModalOpen={setIsModificationModalOpen}
            /> */}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModificationModalOpen}
        onClose={() => setIsModificationModalOpen(false)}
        size="md"
        header={true}
        title="Update Confirmation"
        showFullScreen={false}
      >
        <ModificationCommentModal
          isOpen={isModificationModalOpen}
          onClose={() => setIsModificationModalOpen(false)}
          handleSave={handleSave}
          documentData={documentData}
          documentType={documentType}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          CURRENT_USER={CURRENT_USER}
          BASE_URL={BASE_URL}
          setIsModificationModalOpen={setIsModificationModalOpen}
        />
      </Modal>
    </div>
  );
};

export default LocationTransfer;
